import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {Session} from "../../services/Session";
import {LoadingService} from "../../services/LoadingService";
import {ToastService} from "../../services/Toast.service";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {FormulaTestCaseService} from "../../tpiq-services/formula-test-case.service";
import {buildInputJsonFromFormula, detectFormulaVariables} from "../formula-variable-detector";
import {formulaTypeLabel} from "../formula-type-labels";

@Component({
  selector: 'app-formula-test-case-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './formula-test-case-form.component.html',
  styleUrl: './formula-test-case-form.component.scss'
})
export class FormulaTestCaseFormComponent implements OnInit, OnChanges {
  @Input() editMode = false;
  @Input() testCase: any = null;
  @Output() onCancel = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  org: string;
  user: string;

  model: any = this.blankModel();
  useRuleReference = false;
  availableRules: any[] = [];
  selectedRule: any = null;
  ruleFormulaText = '';
  originalRuleFormula = '';
  inputValuesText = '';
  jsonError = '';
  saving = false;
  testing = false;
  testResult: any = null;

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private formulaTestCaseService: FormulaTestCaseService
  ) {
  }

  ngOnInit(): void {
    this.org = Session.getOrg();
    this.user = Session.getUser().id;
    this.loadAvailableRules();
    this.applyIncomingTestCase();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.testCase && !changes.testCase.firstChange) {
      this.applyIncomingTestCase();
    }
  }

  private blankModel(): any {
    return {
      id: null,
      name: '',
      description: '',
      formulaType: 'BOOLEAN',
      sourceRuleId: null,
      formula: '',
      expectedResult: '',
      active: true
    };
  }

  private applyIncomingTestCase() {
    if (this.testCase) {
      this.model = {...this.testCase};
      this.useRuleReference = !!this.testCase.sourceRuleId;
      this.inputValuesText = this.testCase.inputValues || '';
      if (this.useRuleReference) {
        this.selectedRule = this.availableRules.find(r => r.id === this.testCase.sourceRuleId) || null;
        this.resetRuleFormulaText();
      }
    } else {
      this.model = this.blankModel();
      this.useRuleReference = false;
      this.selectedRule = null;
      this.resetRuleFormulaText();
      this.inputValuesText = '';
    }
  }

  loadAvailableRules() {
    this.formulaTestCaseService.getAvailableRules(this.org, this.user).subscribe((rules) => {
      this.availableRules = rules || [];
      if (this.testCase && this.testCase.sourceRuleId) {
        this.selectedRule = this.availableRules.find(r => r.id === this.testCase.sourceRuleId) || null;
        this.resetRuleFormulaText();
      }
    }, () => {
      this.availableRules = [];
    });
  }

  onRuleSelected() {
    this.model.sourceRuleId = this.selectedRule ? this.selectedRule.id : null;
    this.resetRuleFormulaText();
    this.testResult = null;
    this.inputValuesText = '';
    this.jsonError = '';
    if (this.selectedRule && !this.model.description) {
      this.model.description = `Strategy: ${this.selectedRule.strategyName || 'Unknown'} — Rule: ${this.selectedRule.name}`;
    }
  }

  onFormulaTypeChange() {
    // The rule column being edited depends on the formula type, so switching
    // type discards any unsaved rule-formula edits and reloads the new column.
    this.resetRuleFormulaText();
    this.testResult = null;
  }

  toggleSource(useRule: boolean) {
    this.useRuleReference = useRule;
    if (!useRule) {
      this.selectedRule = null;
      this.model.sourceRuleId = null;
    }
    this.resetRuleFormulaText();
    this.testResult = null;
    this.inputValuesText = '';
    this.jsonError = '';
  }

  private resetRuleFormulaText() {
    this.originalRuleFormula = this.rulePreview();
    this.ruleFormulaText = this.originalRuleFormula;
  }

  ruleFormulaDirty(): boolean {
    return this.useRuleReference && !!this.selectedRule && this.ruleFormulaText !== this.originalRuleFormula;
  }

  rulePreview(): string {
    if (!this.selectedRule) {
      return '';
    }
    if (this.model.formulaType === 'NUMERIC') {
      return this.selectedRule.planAmountFormula || '';
    }
    if (this.model.formulaType === 'STRING') {
      return this.selectedRule.whyDescription || '';
    }
    return this.selectedRule.formula || '';
  }

  effectiveFormula(): string {
    return this.useRuleReference ? this.ruleFormulaText : this.model.formula;
  }

  formulaTypeLabel(type: string): string {
    return formulaTypeLabel(type);
  }

  detectVariables() {
    const formula = this.effectiveFormula();
    if (!formula || !formula.trim()) {
      this.toastService.pop(TOAST_TYPE.error, this.useRuleReference ? 'Select a rule to reference' : 'Formula is required');
      return;
    }
    const variables = detectFormulaVariables(formula);
    if (!variables.length) {
      this.toastService.pop(TOAST_TYPE.info, 'No variables detected in this formula');
      return;
    }
    this.inputValuesText = buildInputJsonFromFormula(formula, this.inputValuesText);
    this.jsonError = '';
  }

  test() {
    const formula = this.effectiveFormula();
    if (!formula || !formula.trim()) {
      this.toastService.pop(TOAST_TYPE.error, this.useRuleReference ? 'Select a rule to reference' : 'Formula is required');
      return;
    }
    if (!this.validateJson()) {
      return;
    }

    const payload = {
      formulaType: this.model.formulaType,
      formula,
      inputValues: this.inputValuesText,
      expectedResult: this.model.expectedResult
    };

    this.testing = true;
    this.testResult = null;
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.evaluateSandbox(this.org, this.user, payload).subscribe((response) => {
      this.testing = false;
      this.loadingService.triggerLoadingEvent(false);
      this.testResult = response;
    }, (error) => {
      this.testing = false;
      this.loadingService.triggerLoadingEvent(false);
      this.testResult = {passed: false, errorMessage: error.message || 'Evaluation failed'};
    });
  }

  validateJson(): boolean {
    if (!this.inputValuesText || !this.inputValuesText.trim()) {
      this.jsonError = '';
      return true;
    }
    try {
      JSON.parse(this.inputValuesText);
      this.jsonError = '';
      return true;
    } catch (e) {
      this.jsonError = 'Input values must be valid JSON';
      return false;
    }
  }

  save() {
    if (!this.model.name || !this.model.name.trim()) {
      this.toastService.pop(TOAST_TYPE.error, 'Name is required');
      return;
    }
    if (this.useRuleReference && !this.model.sourceRuleId) {
      this.toastService.pop(TOAST_TYPE.error, 'Select a rule to reference');
      return;
    }
    if (this.ruleFormulaDirty() && !this.ruleFormulaText.trim()) {
      this.toastService.pop(TOAST_TYPE.error, 'Rule formula cannot be empty');
      return;
    }
    if (!this.useRuleReference && (!this.model.formula || !this.model.formula.trim())) {
      this.toastService.pop(TOAST_TYPE.error, 'Formula is required when not referencing a rule');
      return;
    }
    if (!this.validateJson()) {
      return;
    }

    this.saving = true;
    this.loadingService.triggerLoadingEvent(true);

    if (this.ruleFormulaDirty()) {
      this.formulaTestCaseService.updateRuleFormula(this.org, this.user, this.model.sourceRuleId, {
        formulaType: this.model.formulaType,
        formula: this.ruleFormulaText.trim()
      }).subscribe(() => {
        this.applyRuleFormulaLocally();
        this.saveTestCaseOnly();
      }, (error) => {
        this.saving = false;
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to update rule formula');
      });
    } else {
      this.saveTestCaseOnly();
    }
  }

  // Mirror the saved formula onto the cached rule so re-selecting it (or
  // switching formula types and back) shows the updated value without a refetch.
  private applyRuleFormulaLocally() {
    const saved = this.ruleFormulaText.trim();
    if (this.model.formulaType === 'NUMERIC') {
      this.selectedRule.planAmountFormula = saved;
    } else if (this.model.formulaType === 'STRING') {
      this.selectedRule.whyDescription = saved;
    } else {
      this.selectedRule.formula = saved;
    }
    this.resetRuleFormulaText();
  }

  private saveTestCaseOnly() {
    const payload = {
      ...this.model,
      formula: this.useRuleReference ? null : this.model.formula,
      sourceRuleId: this.useRuleReference ? this.model.sourceRuleId : null,
      inputValues: this.inputValuesText
    };

    this.formulaTestCaseService.saveTestCase(this.org, this.user, payload).subscribe(() => {
      this.saving = false;
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.success, 'Test case saved successfully');
      this.onSave.emit();
    }, (error) => {
      this.saving = false;
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to save test case');
    });
  }

  cancel() {
    this.onCancel.emit();
  }
}
